import React, { useEffect, useState } from "react"
import {
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Button,
    Badge,
    Table,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
    FormGroup,
    Label,
} from "reactstrap"
import { connect } from "react-redux"
import { useParams, Link } from "react-router-dom"

import {
    setBreadcrumbItems,
    getCustomerInvoiceDetail,
} from "../../store/actions"
import { sendWhatsAppDocument } from "../../helpers/fakebackend_helper"
import toastr from "toastr"

const InvoiceDetail = props => {
    const { id } = useParams()
    document.title = "Détail Facture | CRM Entreprise"
    const [sendingWhatsapp, setSendingWhatsapp] = useState(false)

    const breadcrumbItems = [
        { title: "Finances", link: "#" },
        { title: "Factures", link: "/finance/invoices" },
        { title: "Détail", link: "#" },
    ]

    const [modal, setModal] = useState(false)
    const [creditDate, setCreditDate] = useState(new Date().toISOString().split('T')[0])
    const [creditNotes, setCreditNotes] = useState("")
    const [creditItems, setCreditItems] = useState([])

    useEffect(() => {
        props.setBreadcrumbItems("Détail Facture", breadcrumbItems)
        props.getCustomerInvoiceDetail(id)
    }, [id])

    useEffect(() => {
        if (props.invoice && props.invoice.items) {
            setCreditItems(props.invoice.items.map(item => ({
                id: item.id,
                description: item.description,
                original_quantity: item.quantity,
                quantity: item.quantity,
                unit_price: item.unit_price
            })))
        }
    }, [props.invoice])

    const handleSendWhatsApp = async () => {
        if (!props.invoice) return
        setSendingWhatsapp(true)
        const typeLabel = props.invoice.type === 'credit_note' ? 'Avoir' : 'Facture'
        try {
            await sendWhatsAppDocument({
                type: props.invoice.type || 'invoice',
                id: props.invoice.id
            })
            toastr.success(`${typeLabel} envoyée par WhatsApp avec succès`)
        } catch (error) {
            console.error("Erreur envoi WhatsApp:", error)
            toastr.error(error.response?.data?.message || "Erreur lors de l'envoi")
        } finally {
            setSendingWhatsapp(false)
        }
    }

    const toggleModal = () => setModal(!modal)

    const invoice = props.invoice

    const statusColors = {
        'draft': 'secondary',
        'sent': 'info',
        'paid': 'success',
        'partial': 'warning',
        'overdue': 'danger',
        'cancelled': 'dark',
    }

    const statusLabels = {
        'draft': 'Brouillon',
        'sent': 'Envoyée',
        'paid': 'Payée',
        'partial': 'Partielle',
        'overdue': 'Échue',
        'cancelled': 'Annulée',
    }

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-MA', {
            style: 'currency',
            currency: 'MAD'
        }).format(amount)
    }

    if (!invoice) {
        return (
            <div className="container-fluid">
                <Card>
                    <CardBody>
                        <div className="text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </div>
        )
    }

    return (
        <React.Fragment>
            <div className="container-fluid">
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                <div className="d-flex align-items-center justify-content-between mb-4">
                                    <div>
                                        <h4 className="mb-2">{invoice.invoice_number}</h4>
                                        <Badge color={statusColors[invoice.status]} className="font-size-12">
                                            {statusLabels[invoice.status]}
                                        </Badge>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <Link to="/finance/invoices">
                                            <Button color="secondary" size="sm">
                                                <i className="mdi mdi-arrow-left me-1"></i> Retour
                                            </Button>
                                        </Link>
                                        <Button
                                            color="success"
                                            size="sm"
                                            onClick={handleSendWhatsApp}
                                            disabled={sendingWhatsapp}
                                        >
                                            {sendingWhatsapp ? (
                                                <>
                                                    <i className="bx bx-loader bx-spin font-size-16 align-middle me-2"></i>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="mdi mdi-whatsapp me-1"></i>
                                                </>
                                            )}
                                        </Button>
                                        <Button
                                            color="primary"
                                            size="sm"
                                            onClick={() => {
                                                const id = invoice.id;
                                                import("../../helpers/fakebackend_helper").then(helper => {
                                                    helper.getInvoicePdf(id).then(response => {
                                                        const url = window.URL.createObjectURL(new Blob([response]));
                                                        const link = document.createElement('a');
                                                        link.href = url;
                                                        link.setAttribute('download', `Facture-${invoice.invoice_number}.pdf`);
                                                        document.body.appendChild(link);
                                                        link.click();
                                                    });
                                                });
                                            }}
                                        >
                                            <i className="mdi mdi-printer me-1"></i> Télécharger PDF
                                        </Button>

                                        {invoice.type !== 'credit_note' && invoice.status !== 'cancelled' && (
                                            <Button color="danger" size="sm" onClick={toggleModal}>
                                                <i className="mdi mdi-file-undo me-1"></i> Créer un Avoir
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                <hr />

                                <Row className="mt-4">
                                    <Col sm={6}>
                                        <h5 className="font-size-15 mb-3">Client</h5>
                                        <h6 className="font-size-14 mb-1">
                                            {invoice.customer?.first_name} {invoice.customer?.last_name}
                                        </h6>
                                        {invoice.customer?.company_name && (
                                            <p className="mb-1">{invoice.customer?.company_name}</p>
                                        )}
                                        <p className="mb-1">{invoice.customer?.email}</p>
                                        <p className="mb-0">{invoice.customer?.phone}</p>
                                    </Col>
                                    <Col sm={6} className="text-sm-end">
                                        <div>
                                            <p className="mb-2">
                                                <strong>Date facture:</strong> {new Date(invoice.invoice_date).toLocaleDateString('fr-FR')}
                                            </p>
                                            <p className="mb-2">
                                                <strong>Date échéance:</strong> {new Date(invoice.due_date).toLocaleDateString('fr-FR')}
                                            </p>
                                        </div>
                                    </Col>
                                </Row>

                                <div className="py-4">
                                    <h5 className="font-size-15 mb-3">Détails de la facture</h5>
                                    <div className="table-responsive">
                                        <Table className="table-nowrap table-centered mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Description</th>
                                                    <th className="text-end">Qté</th>
                                                    <th className="text-end">Prix unitaire</th>
                                                    <th className="text-end">TVA</th>
                                                    <th className="text-end">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {invoice.items && invoice.items.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>{item.description}</td>
                                                        <td className="text-end">{item.quantity}</td>
                                                        <td className="text-end">{formatCurrency(item.unit_price)}</td>
                                                        <td className="text-end">{item.tax_rate}%</td>
                                                        <td className="text-end">{formatCurrency(item.total)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>

                                    <Row className="mt-4">
                                        <Col sm={6}>
                                            {invoice.notes && (
                                                <div>
                                                    <h6>Notes:</h6>
                                                    <p className="text-muted">{invoice.notes}</p>
                                                </div>
                                            )}
                                        </Col>
                                        <Col sm={6}>
                                            <div className="text-sm-end">
                                                <div className="mb-2">
                                                    <span className="me-3">Sous-total:</span>
                                                    <span className="fw-semibold">{formatCurrency(invoice.subtotal)}</span>
                                                </div>
                                                <div className="mb-2">
                                                    <span className="me-3">TVA:</span>
                                                    <span className="fw-semibold">{formatCurrency(invoice.tax_amount)}</span>
                                                </div>
                                                {invoice.discount_amount > 0 && (
                                                    <div className="mb-2">
                                                        <span className="me-3">Remise:</span>
                                                        <span className="fw-semibold text-success">
                                                            -{formatCurrency(invoice.discount_amount)}
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="border-top pt-2 mb-2">
                                                    <h5 className="font-size-16 mb-0">
                                                        <span className="me-3">Total:</span>
                                                        <span>{formatCurrency(invoice.total)}</span>
                                                    </h5>
                                                </div>
                                                <div className="mb-2">
                                                    <span className="me-3">Payé:</span>
                                                    <span className="fw-semibold text-success">
                                                        {formatCurrency(invoice.paid_amount)}
                                                    </span>
                                                </div>
                                                <div className="mb-2">
                                                    <span className="me-3">Solde:</span>
                                                    <span className={`fw-bold ${invoice.balance > 0 ? 'text-danger' : 'text-success'}`}>
                                                        {formatCurrency(invoice.balance)}
                                                    </span>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>

                                {invoice.payments && invoice.payments.length > 0 && (
                                    <div className="mt-4">
                                        <h5 className="font-size-15 mb-3">Historique des paiements</h5>
                                        <div className="table-responsive">
                                            <Table className="table-nowrap table-centered mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Date</th>
                                                        <th>N° Paiement</th>
                                                        <th>Méthode</th>
                                                        <th>Référence</th>
                                                        <th className="text-end">Montant</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {invoice.payments.map((payment, idx) => (
                                                        <tr key={idx}>
                                                            <td>{new Date(payment.payment_date).toLocaleDateString('fr-FR')}</td>
                                                            <td>{payment.payment_number}</td>
                                                            <td>
                                                                {payment.method === 'bank_transfer' && 'Virement'}
                                                                {payment.method === 'cash' && 'Espèces'}
                                                                {payment.method === 'check' && 'Chèque'}
                                                                {payment.method === 'card' && 'Carte'}
                                                                {payment.method === 'other' && 'Autre'}
                                                            </td>
                                                            <td>{payment.reference || '-'}</td>
                                                            <td className="text-end text-success fw-semibold">
                                                                {formatCurrency(payment.amount)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>

            <Modal isOpen={modal} toggle={toggleModal} size="lg">
                <ModalHeader toggle={toggleModal}>Créer un Avoir pour {invoice.invoice_number}</ModalHeader>
                <ModalBody>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Date de l'avoir</Label>
                                <Input
                                    type="date"
                                    value={creditDate}
                                    onChange={(e) => setCreditDate(e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Table responsive className="mt-3">
                        <thead>
                            <tr>
                                <th>Description</th>
                                <th>Qté Originale</th>
                                <th>Qté à Créditer</th>
                                <th>Prix Unit.</th>
                                <th>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {creditItems.map((item, index) => (
                                <tr key={index}>
                                    <td>{item.description}</td>
                                    <td>{item.original_quantity}</td>
                                    <td>
                                        <Input
                                            type="number"
                                            min="0"
                                            max={item.original_quantity}
                                            value={item.quantity}
                                            onChange={(e) => {
                                                const newItems = [...creditItems];
                                                newItems[index].quantity = parseInt(e.target.value) || 0;
                                                setCreditItems(newItems);
                                            }}
                                        />
                                    </td>
                                    <td>{formatCurrency(item.unit_price)}</td>
                                    <td>{formatCurrency(item.quantity * item.unit_price)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <FormGroup className="mt-3">
                        <Label>Notes / Raison</Label>
                        <Input
                            type="textarea"
                            rows="3"
                            value={creditNotes}
                            placeholder="Ex: Erreur de facturation, Retour marchandise..."
                            onChange={(e) => setCreditNotes(e.target.value)}
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleModal}>Annuler</Button>
                    <Button color="danger" onClick={() => {
                        const data = {
                            invoice_date: creditDate,
                            notes: creditNotes || `Avoir pour la facture ${invoice.invoice_number}`,
                            items: creditItems.filter(i => i.quantity > 0).map(i => ({
                                id: i.id,
                                quantity: i.quantity
                            }))
                        };

                        if (data.items.length === 0) {
                            alert("Veuillez sélectionner au moins un article avec une quantité supérieure à 0.");
                            return;
                        }

                        import("../../helpers/fakebackend_helper").then(helper => {
                            helper.addCreditNote(invoice.id, data).then(() => {
                                toggleModal();
                                props.getCustomerInvoiceDetail(invoice.id);
                            }).catch(err => {
                                alert("Erreur lors de la création de l'avoir");
                            });
                        });
                    }}>
                        Générer l'Avoir
                    </Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    )
}

const mapStateToProps = state => ({
    invoice: state.Invoices.invoice,
})

export default connect(mapStateToProps, {
    setBreadcrumbItems,
    getCustomerInvoiceDetail,
})(InvoiceDetail)
